import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { Search, RefreshCw, FileDown, Eye, X, Send, CheckCircle2, Lock, Rocket, Sparkles, Camera, Upload, Trash2, FileSearch } from "lucide-react";

function tierBadge(label) {
  const c = {
    Invisible:           "#c46a5a",
    "Under-represented": "#d4a04a",
    "Underrepresented": "#d4a04a",
    Emerging:            "#b8963e",
    Recommended:         "#6db66d",
    "Category Authority":"#6db66d",
    "Dominant": "#6db66d",
  }[label] || "#5e5448";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-mono-eav tracking-wider" style={{ color: c }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {(label || "—").toUpperCase()}
    </span>
  );
}

function warningBadge(audit) {
  const n = audit?.validation_warnings_count || 0;
  if (n <= 0) return null;
  const warnings = audit?.validation_warnings || [];
  const tooltip = warnings.length
    ? warnings.join("\n\n")
    : `${n} scoring caps were applied to this audit`;
  return (
    <span
      data-testid={`admin-caps-badge-${audit.id}`}
      title={tooltip}
      className="inline-flex items-center gap-1 text-[10px] font-mono-eav tracking-wider font-bold px-1.5 py-0.5 rounded-sm cursor-help"
      style={{ color: "#d4a04a", background: "rgba(212, 160, 74, 0.12)", border: "1px solid rgba(212, 160, 74, 0.4)" }}
    >
      ⚠ {n} CAP{n > 1 ? "S" : ""}
    </span>
  );
}

export default function Admin() {
  const [audits, setAudits] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [openResponses, setOpenResponses] = useState(null);
  const [sendModal, setSendModal] = useState(null); // {audit} — open send/approve dialog
  const [newFullModal, setNewFullModal] = useState(false); // open full-audit intake
  const [evidenceModal, setEvidenceModal] = useState(null); // {audit} — open evidence upload
  const [previewModal, setPreviewModal]   = useState(null); // {audit} — inline PDF preview

  const load = async () => {
    try {
      const [a, s] = await Promise.all([
        api.get(`/admin/audits${q ? `?q=${encodeURIComponent(q)}` : ""}`),
        api.get("/admin/stats"),
      ]);
      setAudits(a.data);
      setStats(s.data);
    } catch { /* admin list fetch failed — leave previous state */ }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSearch = (e) => { e.preventDefault(); load(); };
  const rerun = async (id) => { await api.post(`/audits/${id}/rerun`); setTimeout(load, 500); };
  const downloadPdf = async (id, company) => {
    const token = localStorage.getItem("eav_token");
    const res = await fetch(`${API}/audits/${id}/pdf`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return alert("PDF not ready");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${company.replace(/\s+/g, "_")}_Snapshot.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const viewResponses = async (id) => {
    const { data } = await api.get(`/admin/audits/${id}/responses`);
    setOpenResponses({ id, items: data.responses || [] });
  };
  const updateStatus = async (id, status) => {
    await api.patch(`/admin/audits/${id}`, { status });
    load();
  };
  const sendToCustomer = async (id, calendarUrl) => {
    // Called by the SendDeliveryModal. /admin/audits/{id}/send implicitly
    // sets admin_approved_at when audit_kind='full' is unapproved (send=approve).
    const { data } = await api.post(`/admin/audits/${id}/send`, { calendar_url: calendarUrl || "" });
    return data;
  };

  // One-click admin upgrade for a completed snapshot row. Hits the existing
  // /upgrade-to-full endpoint which, for an admin caller, mints a Full Audit
  // doc with admin_bypass=true and immediately starts the LLM pipeline.
  const upgradeToFull = async (snapshotId, companyName) => {
    if (!window.confirm(`Run a Full AI Visibility Audit for ${companyName}?\n\nAdmin bypass — no Stripe, no payment required. It will start running immediately (takes ~2 minutes).`)) return;
    try {
      const { data } = await api.post(`/audits/${snapshotId}/upgrade-to-full`);
      alert(`Full Audit started ✓\nNew audit id: ${data.audit_id}\nStatus: ${data.status}\n\nIt will appear in the list above once running. Reload in ~2 minutes for the completed deliverable.`);
      load();
    } catch (e) {
      alert(`Could not start Full Audit: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Full Audit approval gate — flips admin_approved_at so the customer's
  // PDF download endpoint stops returning 403. Spec: "Final PDF cannot
  // export until admin approves."
  const toggleApproval = async (id, currentlyApproved) => {
    try {
      await api.post(`/admin/audits/${id}/approve`, { approved: !currentlyApproved });
      load();
    } catch (e) {
      alert(`Approval failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14" data-testid="admin-page">
      <p className="overline">Admin</p>
      <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#f4efe6] leading-[1.05]" style={{fontWeight: 700}}>
        Audit <em className="text-[#b8963e] italic font-normal">operations.</em>
      </h1>
      <p className="mt-3 text-[#c9bca4]">Inspect every audit, review raw model responses, re-run snapshots, export PDFs.</p>

      {/* Admin quick-nav */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-quick-nav">
        <Link to="/pipeline"
              className="px-3 py-1.5 text-xs rounded-sm font-mono-eav tracking-wider uppercase border border-[#2a2620] text-[#c9bca4] hover:border-[#b8963e] hover:text-[#b8963e]">
          Outreach pipeline
        </Link>
        <Link to="/admin/publications"
              data-testid="admin-nav-publications"
              className="px-3 py-1.5 text-xs rounded-sm font-mono-eav tracking-wider uppercase border border-[#2a2620] text-[#c9bca4] hover:border-[#b8963e] hover:text-[#b8963e]">
          Publications CMS
        </Link>
        <Link to="/admin/payments"
              data-testid="admin-nav-payments"
              className="px-3 py-1.5 text-xs rounded-sm font-mono-eav tracking-wider uppercase border border-[#2a2620] text-[#c9bca4] hover:border-[#b8963e] hover:text-[#b8963e]">
          Payments
        </Link>
        <Link to="/admin/benchmarks"
              data-testid="admin-nav-benchmarks"
              className="px-3 py-1.5 text-xs rounded-sm font-mono-eav tracking-wider uppercase border border-[#2a2620] text-[#c9bca4] hover:border-[#b8963e] hover:text-[#b8963e]">
          Industry benchmarks
        </Link>
        <button
          onClick={() => setNewFullModal(true)}
          data-testid="admin-nav-new-full-audit"
          className="px-3 py-1.5 text-xs rounded-sm font-mono-eav tracking-wider uppercase border border-[#b8963e] bg-[#b8963e]/10 text-[#b8963e] hover:bg-[#b8963e]/20">
          + Start Full Audit
        </button>
        <button
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/llm/run-self-audit");
              alert(`Self-audit queued (id ${data.audit_id}). It will appear on /llm once complete (~2 min).`);
            } catch (e) {
              alert("Could not queue self-audit. Check the backend logs.");
            }
          }}
          data-testid="admin-nav-self-audit"
          className="px-3 py-1.5 text-xs rounded-sm font-mono-eav tracking-wider uppercase border border-[#b8963e] text-[#b8963e] hover:bg-[#b8963e]/10">
          Run /llm self-audit
        </button>
      </div>

      {/* Stats */}
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-stats">
        {[
          ["Audits Created",       stats?.audits_total,    "All audits run through the platform"],
          ["Snapshots Complete",   stats?.audits_complete, "Reports ready to deliver"],
          ["Failed / Errored",     stats?.audits_failed,   "Need a re-run before sending"],
          ["Registered Customers", stats?.users_total,     "User accounts in the system"],
        ].map(([t, v, sub]) => (
          <div key={t} className="eav-card p-5" data-testid={`stat-card-${t.replace(/\s+/g,'-').toLowerCase()}`}>
            <p className="text-[11px] font-mono-eav tracking-[0.18em] uppercase font-bold" style={{ color: "#b8963e" }}>
              {t}
            </p>
            <p className="mt-3 font-serif-display font-bold text-4xl text-[#f4efe6] leading-none">{v ?? "—"}</p>
            <p className="mt-3 text-xs text-[#8a7e6d] leading-snug">{sub}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mt-10 flex gap-2">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-[#5e5448] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="admin-search-input"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by company name…"
                 className="eav-input pl-10" />
        </div>
        <button type="submit" data-testid="admin-search-button" className="btn-gold">
          Search
        </button>
      </form>

      {/* Table */}
      <div className="mt-6 eav-card overflow-hidden">
        {/* Column headers — hidden on mobile */}
        <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-[#2a2620] bg-[#0a0908] font-mono-eav text-[10px] tracking-[0.2em] text-[#8a7e6d] uppercase">
          <div className="col-span-3">Company</div>
          <div className="col-span-2">Industry</div>
          <div className="col-span-1">Score</div>
          <div className="col-span-2">Tier</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {!audits && <div className="p-6 text-[#8a7e6d] font-mono-eav text-sm">Loading…</div>}
        {audits && audits.length === 0 && <div className="p-14 text-center text-[#5e5448] font-mono-eav">No audits yet.</div>}
        {audits && audits.map((a) => (
          <div key={a.id} data-testid={`admin-row-${a.id}`}
               className="border-b border-[#1a1815]">
            {/* Desktop / tablet — original grid layout */}
            <div className="hidden md:grid grid-cols-12 px-6 py-4 items-center text-sm">
              <div className="col-span-3">
                <div className="font-medium text-[#f4efe6] flex items-center gap-2">
                  {a.company_name}
                  {a.admin_bypass && (
                    <span
                      data-testid={`admin-bypass-badge-${a.id}`}
                      title="Admin bypass — never billed via Stripe"
                      className="px-1.5 py-0.5 rounded-sm font-mono-eav text-[9px] tracking-[0.15em] uppercase font-bold"
                      style={{ background: "rgba(184, 150, 62, 0.15)", color: "#b8963e", border: "1px solid rgba(184, 150, 62, 0.5)" }}
                    >
                      Admin · Free
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#5e5448] font-mono-eav mt-0.5">{a.website}</div>
              </div>
              <div className="col-span-2 text-[#c9bca4]">{a.industry}</div>
              <div className="col-span-1 font-serif-display font-bold text-xl text-[#f4efe6]">{a.total_score ?? "—"}</div>
              <div className="col-span-2">{tierBadge(a.tier_label)}{warningBadge(a)}</div>
              <div className="col-span-1">
                <select value={a.status} onChange={(e) => updateStatus(a.id, e.target.value)}
                        data-testid={`admin-status-select-${a.id}`}
                        className="text-xs bg-[#121110] border border-[#2a2620] rounded-sm px-2 py-1 text-[#c9bca4] focus:border-[#b8963e] focus:outline-none">
                  {["pending_review", "queued", "running", "complete", "failed", "archived"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-3 flex justify-end gap-1">
                <Link to={`/snapshot/${a.id}`} data-testid={`admin-view-${a.id}`}
                      className="p-2 hover:bg-[#1a1815] hover:text-[#b8963e] rounded-sm text-[#8a7e6d] transition-colors" title="View">
                  <Eye className="w-4 h-4" />
                </Link>
                <button onClick={() => viewResponses(a.id)} data-testid={`admin-responses-${a.id}`}
                        className="px-2 py-1 text-xs font-mono-eav hover:bg-[#1a1815] hover:text-[#b8963e] rounded-sm text-[#8a7e6d] transition-colors" title="Raw">
                  RAW
                </button>
                <button onClick={() => rerun(a.id)} data-testid={`admin-rerun-${a.id}`}
                        className="p-2 hover:bg-[#1a1815] hover:text-[#b8963e] rounded-sm text-[#8a7e6d] transition-colors" title="Re-run">
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button onClick={() => setPreviewModal({ audit: a })} data-testid={`admin-preview-${a.id}`}
                        disabled={a.status !== 'complete'}
                        className="p-2 hover:bg-[#1a1815] hover:text-[#b8963e] rounded-sm text-[#8a7e6d] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Preview the deliverable PDF inline (no download, no email)">
                  <FileSearch className="w-4 h-4" />
                </button>
                <button onClick={() => downloadPdf(a.id, a.company_name)} data-testid={`admin-pdf-${a.id}`}
                        className="p-2 hover:bg-[#1a1815] hover:text-[#b8963e] rounded-sm text-[#8a7e6d] transition-colors" title="PDF">
                  <FileDown className="w-4 h-4" />
                </button>
                {a.audit_kind === "snapshot" && a.status === "complete" && (
                  <button onClick={() => upgradeToFull(a.id, a.company_name)}
                          data-testid={`admin-upgrade-${a.id}`}
                          title={`Run a Full AI Visibility Audit for ${a.company_name} using this snapshot's data (admin bypass — no Stripe, runs immediately)`}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-sm border border-[#b8963e] bg-[#b8963e]/15 text-[#b8963e] hover:bg-[#b8963e]/25 transition-colors">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-mono-eav tracking-wider uppercase">+ Full</span>
                  </button>
                )}
                {a.audit_kind === "full" && a.status === "complete" && (
                  <button onClick={() => setEvidenceModal({ audit: a })}
                          data-testid={`admin-evidence-${a.id}`}
                          title="Upload visual evidence (screenshots) for the Evidence Appendix"
                          className="p-2 rounded-sm transition-colors text-[#b8963e] hover:bg-[#b8963e]/10">
                    <Camera className="w-4 h-4" />
                  </button>
                )}
                {a.audit_kind === "full" && a.status === "complete" && (
                  <button onClick={() => toggleApproval(a.id, !!a.admin_approved_at)}
                          data-testid={`admin-approve-${a.id}`}
                          title={a.admin_approved_at
                                  ? `Approved on ${new Date(a.admin_approved_at).toLocaleString()}. Click to revoke.`
                                  : "Approve this Full Audit for customer PDF delivery"}
                          className={`p-2 rounded-sm transition-colors ${a.admin_approved_at ? "text-[#6db66d] hover:bg-[#1a1815]" : "text-[#d4a04a] hover:bg-[#1a1815] hover:text-[#b8963e]"}`}>
                    {a.admin_approved_at ? <CheckCircle2 className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  </button>
                )}
                <button onClick={() => setSendModal({ audit: a })} data-testid={`admin-send-${a.id}`}
                        disabled={a.status !== 'complete'}
                        className="p-2 hover:bg-[#1a1815] hover:text-[#b8963e] rounded-sm text-[#8a7e6d] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={(a.audit_kind === "full" && !a.admin_approved_at) ? "Approve & email customer" : "Send to customer"}>
                  {(a.audit_kind === "full" && !a.admin_approved_at) ? <Rocket className="w-4 h-4 text-[#d4a04a]" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Mobile — stacked card layout */}
            <div className="md:hidden px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#f4efe6] truncate">{a.company_name}</div>
                  <div className="text-[11px] text-[#5e5448] font-mono-eav mt-0.5 truncate">{a.website}</div>
                  <div className="mt-1 text-xs text-[#8a7e6d]">{a.industry}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-serif-display font-bold text-3xl text-[#f4efe6] leading-none">
                    {a.total_score ?? "—"}
                  </div>
                  <div className="text-[10px] font-mono-eav text-[#5e5448] mt-1">/ 100</div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {tierBadge(a.tier_label)}{warningBadge(a)}
                <select value={a.status} onChange={(e) => updateStatus(a.id, e.target.value)}
                        data-testid={`admin-status-select-mobile-${a.id}`}
                        className="text-xs bg-[#121110] border border-[#2a2620] rounded-sm px-2 py-1 text-[#c9bca4] focus:border-[#b8963e] focus:outline-none">
                  {["pending_review", "queued", "running", "complete", "failed", "archived"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="mt-3 grid grid-cols-5 gap-1">
                <Link to={`/snapshot/${a.id}`}
                      className="flex flex-col items-center gap-1 py-2 rounded-sm bg-[#121110] hover:bg-[#1a1815] text-[#8a7e6d] hover:text-[#b8963e] transition-colors" title="View">
                  <Eye className="w-4 h-4" />
                  <span className="text-[9px] font-mono-eav tracking-wider">VIEW</span>
                </Link>
                <button onClick={() => viewResponses(a.id)}
                        className="flex flex-col items-center gap-1 py-2 rounded-sm bg-[#121110] hover:bg-[#1a1815] text-[#8a7e6d] hover:text-[#b8963e] transition-colors" title="Raw">
                  <Search className="w-4 h-4" />
                  <span className="text-[9px] font-mono-eav tracking-wider">RAW</span>
                </button>
                <button onClick={() => rerun(a.id)}
                        className="flex flex-col items-center gap-1 py-2 rounded-sm bg-[#121110] hover:bg-[#1a1815] text-[#8a7e6d] hover:text-[#b8963e] transition-colors" title="Re-run">
                  <RefreshCw className="w-4 h-4" />
                  <span className="text-[9px] font-mono-eav tracking-wider">RERUN</span>
                </button>
                <button onClick={() => downloadPdf(a.id, a.company_name)}
                        className="flex flex-col items-center gap-1 py-2 rounded-sm bg-[#121110] hover:bg-[#1a1815] text-[#8a7e6d] hover:text-[#b8963e] transition-colors" title="PDF">
                  <FileDown className="w-4 h-4" />
                  <span className="text-[9px] font-mono-eav tracking-wider">PDF</span>
                </button>
                <button onClick={() => setSendModal({ audit: a })}
                        disabled={a.status !== 'complete'}
                        className="flex flex-col items-center gap-1 py-2 rounded-sm bg-[#121110] hover:bg-[#1a1815] text-[#8a7e6d] hover:text-[#b8963e] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={(a.audit_kind === "full" && !a.admin_approved_at) ? "Approve & email" : "Send"}>
                  {(a.audit_kind === "full" && !a.admin_approved_at) ? <Rocket className="w-4 h-4 text-[#d4a04a]" /> : <Send className="w-4 h-4" />}
                  <span className="text-[9px] font-mono-eav tracking-wider">{(a.audit_kind === "full" && !a.admin_approved_at) ? "APPROVE & EMAIL" : "SEND"}</span>
                </button>
              </div>
              {a.audit_kind === "snapshot" && a.status === "complete" && (
                <button onClick={() => upgradeToFull(a.id, a.company_name)}
                        className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-sm border border-[#b8963e] bg-[#b8963e]/15 text-[#b8963e] hover:bg-[#b8963e]/25 transition-colors">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-[11px] font-mono-eav tracking-wider uppercase font-bold">+ Convert to Full Audit</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Raw responses modal */}
      {openResponses && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
             onClick={() => setOpenResponses(null)} data-testid="admin-responses-modal">
          <div className="eav-card max-w-4xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#121110] border-b border-[#2a2620] px-6 py-4 flex justify-between items-center">
              <p className="font-serif-display font-bold text-lg text-[#f4efe6]">Raw AI Responses</p>
              <button onClick={() => setOpenResponses(null)} data-testid="admin-responses-close"
                      className="p-1 hover:bg-[#1a1815] rounded-sm text-[#8a7e6d]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {openResponses.items.map((r, i) => (
                <div key={i} className="border border-[#2a2620] rounded-sm p-4">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono-eav font-bold uppercase text-[#b8963e]">{r.provider}</span>
                    <span className="text-[#5e5448]">·</span>
                    <span className="text-[#c9bca4]">{r.prompt_label}</span>
                  </div>
                  <p className="mt-2 font-mono-eav text-xs text-[#5e5448]">{r.prompt_text}</p>
                  <pre className="mt-3 whitespace-pre-wrap text-sm text-[#c9bca4] bg-[#0a0908] border border-[#1a1815] rounded-sm p-3 font-mono-eav">
                    {r.error ? `ERROR: ${r.error}` : r.response}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <SendDeliveryModal
        open={!!sendModal}
        audit={sendModal?.audit}
        onClose={() => setSendModal(null)}
        onSend={sendToCustomer}
        onDone={() => { setSendModal(null); load(); }}
      />

      <StartFullAuditModal
        open={newFullModal}
        onClose={() => setNewFullModal(false)}
        onDone={() => { setNewFullModal(false); load(); }}
      />

      <EvidenceModal
        open={!!evidenceModal}
        audit={evidenceModal?.audit}
        onClose={() => setEvidenceModal(null)}
      />

      <PreviewPdfModal
        open={!!previewModal}
        audit={previewModal?.audit}
        onClose={() => setPreviewModal(null)}
      />
    </div>
  );
}

/**
 * Combined "Approve & Email" / "Send" delivery dialog. For unapproved full
 * audits the primary CTA reads "Approve & Email customer" so the admin
 * understands one click does both — the /admin/audits/{id}/send endpoint
 * implicitly flips admin_approved_at. For snapshots or already-approved
 * full audits the CTA reads "Send to customer".
 */
function SendDeliveryModal({ open, audit, onClose, onSend, onDone }) {
  const [calendarUrl, setCalendarUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  React.useEffect(() => {
    if (open) {
      setCalendarUrl("");
      setSending(false);
      setError("");
      setSuccess(null);
    }
  }, [open]);

  if (!open || !audit) return null;

  const isUnapprovedFull = audit.audit_kind === "full" && !audit.admin_approved_at;
  const ctaLabel = isUnapprovedFull ? "Approve & email customer" : "Send to customer";

  const onSubmit = async () => {
    setSending(true);
    setError("");
    try {
      const data = await onSend(audit.id, calendarUrl.trim());
      setSuccess(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="send-delivery-modal"
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="eav-card p-7 max-w-md w-full border border-[#2a2620] bg-[#0c0a08]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="overline text-[#b8963e]">
              {isUnapprovedFull ? "Approve & Deliver" : "Deliver"}
            </p>
            <h3 className="mt-2 font-serif-display text-2xl text-[#f4efe6] leading-tight" style={{ fontWeight: 600 }}>
              {audit.company_name}
            </h3>
            <p className="mt-1 font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">
              {audit.audit_kind === "full" ? "Full AI Visibility Audit" : "AI Visibility Snapshot"}
            </p>
          </div>
          <button onClick={onClose} data-testid="send-modal-close"
                   className="p-1 hover:bg-[#1a1815] rounded-sm text-[#8a7e6d]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-2 text-[#6db66d]">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-mono-eav text-sm tracking-wider uppercase">Delivered</span>
            </div>
            <p className="text-sm text-[#c9bca4]">
              Sent to <span className="text-[#f4efe6]">{success.to}</span>.
              {isUnapprovedFull && (
                <span className="block mt-2 text-xs text-[#b8963e]">
                  Approval gate flipped — customer can now re-download from /my-snapshot.
                </span>
              )}
            </p>
            <button onClick={onDone} data-testid="send-modal-done"
                     className="btn-gold text-sm w-full justify-center mt-2">Done</button>
          </div>
        ) : (
          <>
            {isUnapprovedFull && (
              <div className="mt-5 px-3 py-2 border-l-2 border-[#b8963e]/60 bg-[#b8963e]/8 text-xs text-[#c9bca4] leading-snug">
                This single action will <strong className="text-[#f4efe6]">flip the approval gate</strong> AND
                <strong className="text-[#f4efe6]"> email the customer</strong> their full audit PDF in one step.
              </div>
            )}

            <label className="block mt-5">
              <span className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">
                Calendar link (optional)
              </span>
              <input
                type="url"
                value={calendarUrl}
                onChange={(e) => setCalendarUrl(e.target.value)}
                data-testid="send-modal-calendar-input"
                placeholder="https://calendly.com/your-team/30min"
                className="mt-2 w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] focus:outline-none rounded-sm px-3 py-2 text-sm text-[#f4efe6] placeholder-[#5e5448]"
              />
              <span className="mt-1 block text-[11px] text-[#5e5448]">
                Included as the booking CTA in the delivery email. Leave empty to skip.
              </span>
            </label>

            {error && (
              <div className="mt-4 text-xs text-[#c46a5a] bg-[#c46a5a]/10 border border-[#c46a5a]/30 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}

            <button
              onClick={onSubmit}
              disabled={sending}
              data-testid="send-modal-submit"
              className="btn-gold text-sm w-full justify-center mt-5"
            >
              {isUnapprovedFull ? <Rocket className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {sending ? "Working…" : ctaLabel}
            </button>
          </>
        )}
      </div>
    </div>
  );
}


/**
 * Admin-only "Start Full Audit" intake modal. Bypasses Stripe + the
 * snapshot-required gate (admin caller of POST /api/audits is admin_bypass=true).
 * Runs the 32-call LLM pipeline immediately (~2 minutes).
 */
function StartFullAuditModal({ open, onClose, onDone }) {
  const empty = {
    company_name: "",
    website: "",
    industry: "",
    region: "United States",
    competitors: ["", "", "", "", ""],
    top_services: ["", "", ""],
    primary_buyer: "",
    homepage_headline: "",
    about_paragraph: "",
    category_phrase: "",
    booking_url: "",
    revenue_mode: "narrative",  // Fix 3: verified | narrative | conservative
    revenue_calibration: {
      avg_deal_size_usd: "",
      monthly_buyer_intent_volume: "",
      close_rate: "",
      sales_cycle_days: "",
      geography: "",
      service_category: "",
      visibility_loss_severity: "",
    },
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => { if (open) { setForm(empty); setBusy(false); setError(""); } }, [open]); // eslint-disable-line

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateArr = (k, i, v) => setForm((f) => {
    const next = [...f[k]];
    next[i] = v;
    return { ...f, [k]: next };
  });

  const submit = async () => {
    setError("");
    const clean = {
      ...form,
      competitors:   form.competitors.map((c) => c.trim()).filter(Boolean),
      top_services:  form.top_services.map((s) => s.trim()).filter(Boolean),
    };
    if (!clean.company_name.trim()) { setError("Company name is required."); return; }
    if (!clean.website.trim())      { setError("Website is required."); return; }
    if (!clean.industry.trim())     { setError("Industry is required."); return; }
    if (clean.competitors.length < 5)  { setError("At least 5 competitors are required for a Full Audit."); return; }
    if (clean.top_services.length < 3) { setError("At least 3 top services are required for a Full Audit."); return; }
    if (!clean.primary_buyer.trim())   { setError("Primary buyer is required."); return; }

    setBusy(true);
    try {
      // Coerce numeric calibration fields to integers/decimals; leave empty
      // strings as undefined so the backend treats them as UNVERIFIED.
      const cal = clean.revenue_calibration || {};
      const numericCal = {
        avg_deal_size_usd:          cal.avg_deal_size_usd ? Number(cal.avg_deal_size_usd) : undefined,
        monthly_buyer_intent_volume: cal.monthly_buyer_intent_volume ? Number(cal.monthly_buyer_intent_volume) : undefined,
        close_rate:                 cal.close_rate ? Number(cal.close_rate) : undefined,
        sales_cycle_days:           cal.sales_cycle_days ? Number(cal.sales_cycle_days) : undefined,
        geography:                  (cal.geography || "").trim() || undefined,
        service_category:           (cal.service_category || "").trim() || undefined,
        visibility_loss_severity:   (cal.visibility_loss_severity || "").trim() || undefined,
      };
      const hasAnyCal = Object.values(numericCal).some((v) => v !== undefined && !Number.isNaN(v));
      const payload = {
        ...clean,
        audit_kind: "full",
        mode: "auto",
        booking_url: (clean.booking_url || "").trim() || undefined,
        revenue_mode: clean.revenue_mode || undefined,
        revenue_calibration: hasAnyCal ? numericCal : undefined,
      };
      const { data } = await api.post("/audits", payload);
      alert(`Full Audit started.\nNew audit id: ${data.id}\nStatus: ${data.status}\n\nIt will appear in the list and complete in ~2 minutes. As admin, you bypass Stripe and the snapshot-required gate.`);
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to start audit.");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="start-full-audit-modal"
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="eav-card p-7 max-w-3xl w-full max-h-[92vh] overflow-y-auto border border-[#b8963e]/40 bg-[#0c0a08]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="overline text-[#b8963e]">Admin · Run Full AI Visibility Audit</p>
            <h3 className="mt-2 font-serif-display text-3xl text-[#f4efe6] leading-tight" style={{ fontWeight: 600 }}>
              Manual Full Audit
            </h3>
            <p className="mt-2 text-sm text-[#c9bca4] max-w-xl">
              Bypasses Stripe and the snapshot-required gate. Starts the 32-call LLM pipeline immediately
              and runs for ~2 minutes. You can download the PDF the moment it completes.
            </p>
          </div>
          <button onClick={onClose} data-testid="start-full-audit-close"
                   className="p-1 hover:bg-[#1a1815] rounded-sm text-[#8a7e6d]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <ModalField label="Company name *">
            <input value={form.company_name} onChange={(e) => update("company_name", e.target.value)}
                   data-testid="sfa-company"
                   className="modal-input" placeholder="Acme Industries" />
          </ModalField>
          <ModalField label="Website *">
            <input value={form.website} onChange={(e) => update("website", e.target.value)}
                   data-testid="sfa-website"
                   className="modal-input" placeholder="https://acme.com" />
          </ModalField>
          <ModalField label="Industry *">
            <input value={form.industry} onChange={(e) => update("industry", e.target.value)}
                   data-testid="sfa-industry"
                   className="modal-input" placeholder="Industrial Automation" />
          </ModalField>
          <ModalField label="Region">
            <input value={form.region} onChange={(e) => update("region", e.target.value)}
                   data-testid="sfa-region"
                   className="modal-input" />
          </ModalField>
          <ModalField label="Category phrase (optional)">
            <input value={form.category_phrase} onChange={(e) => update("category_phrase", e.target.value)}
                   data-testid="sfa-category"
                   className="modal-input"
                   placeholder="e.g. factory automation integrators" />
          </ModalField>
          <ModalField label="Primary buyer *">
            <input value={form.primary_buyer} onChange={(e) => update("primary_buyer", e.target.value)}
                   data-testid="sfa-buyer"
                   className="modal-input"
                   placeholder="VP of Operations at mid-market manufacturers" />
          </ModalField>
        </div>

        <ModalField label="Homepage headline" className="mt-4">
          <input value={form.homepage_headline} onChange={(e) => update("homepage_headline", e.target.value)}
                 data-testid="sfa-headline"
                 className="modal-input"
                 placeholder="Automation for the modern factory floor." />
        </ModalField>
        <ModalField label="About paragraph" className="mt-4">
          <textarea value={form.about_paragraph} onChange={(e) => update("about_paragraph", e.target.value)}
                     data-testid="sfa-about" rows={3}
                     className="modal-input"
                     placeholder="Two- or three-sentence company description." />
        </ModalField>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <p className="overline text-[#b8963e]">Competitors (need 5+)</p>
            <div className="mt-2 space-y-2">
              {form.competitors.map((c, i) => (
                <input key={i} value={c} onChange={(e) => updateArr("competitors", i, e.target.value)}
                       data-testid={`sfa-competitor-${i}`}
                       placeholder={`Competitor ${i + 1}`}
                       className="modal-input" />
              ))}
              <button type="button" onClick={() => update("competitors", [...form.competitors, ""])}
                       className="text-xs text-[#b8963e] hover:underline">+ add another</button>
            </div>
          </div>
          <div>
            <p className="overline text-[#b8963e]">Top services (need 3+)</p>
            <div className="mt-2 space-y-2">
              {form.top_services.map((s, i) => (
                <input key={i} value={s} onChange={(e) => updateArr("top_services", i, e.target.value)}
                       data-testid={`sfa-service-${i}`}
                       placeholder={`Service ${i + 1}`}
                       className="modal-input" />
              ))}
              <button type="button" onClick={() => update("top_services", [...form.top_services, ""])}
                       className="text-xs text-[#b8963e] hover:underline">+ add another</button>
            </div>
          </div>
        </div>

        {/* Fix 5 — Revenue calibration inputs (optional). Empty fields produce a directional estimate instead of a fabricated $ figure. */}
        <div className="mt-6 p-4 border border-[#b8963e]/30 bg-[#b8963e]/5 rounded-sm">
          <p className="overline text-[#b8963e]">Revenue calibration <span className="opacity-70">(optional)</span></p>
          <p className="mt-1 text-[11px] text-[#8a7e6d] leading-snug">
            Leave any field blank to flag the Revenue Impact section as <strong>directional only</strong> instead of a hard dollar figure.
          </p>
          <ModalField label="Revenue mode" className="mt-3">
            <select value={form.revenue_mode}
                     onChange={(e) => update("revenue_mode", e.target.value)}
                     data-testid="sfa-cal-mode" className="modal-input">
              <option value="verified">Verified Inputs — render formula + numbers</option>
              <option value="narrative">Directional Risk Narrative — no $ figure</option>
              <option value="conservative">Conservative Estimate — standard assumptions</option>
            </select>
          </ModalField>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <ModalField label="Avg deal size (USD)">
              <input type="number" value={form.revenue_calibration.avg_deal_size_usd}
                     onChange={(e) => update("revenue_calibration", { ...form.revenue_calibration, avg_deal_size_usd: e.target.value })}
                     data-testid="sfa-cal-deal" className="modal-input" placeholder="50000" />
            </ModalField>
            <ModalField label="Monthly buyer-intent volume">
              <input type="number" value={form.revenue_calibration.monthly_buyer_intent_volume}
                     onChange={(e) => update("revenue_calibration", { ...form.revenue_calibration, monthly_buyer_intent_volume: e.target.value })}
                     data-testid="sfa-cal-volume" className="modal-input" placeholder="200" />
            </ModalField>
            <ModalField label="Close rate (0–1)">
              <input type="number" step="0.01" value={form.revenue_calibration.close_rate}
                     onChange={(e) => update("revenue_calibration", { ...form.revenue_calibration, close_rate: e.target.value })}
                     data-testid="sfa-cal-close" className="modal-input" placeholder="0.12" />
            </ModalField>
            <ModalField label="Sales cycle (days)">
              <input type="number" value={form.revenue_calibration.sales_cycle_days}
                     onChange={(e) => update("revenue_calibration", { ...form.revenue_calibration, sales_cycle_days: e.target.value })}
                     data-testid="sfa-cal-cycle" className="modal-input" placeholder="45" />
            </ModalField>
            <ModalField label="Visibility loss severity">
              <select value={form.revenue_calibration.visibility_loss_severity}
                      onChange={(e) => update("revenue_calibration", { ...form.revenue_calibration, visibility_loss_severity: e.target.value })}
                      data-testid="sfa-cal-severity"
                      className="modal-input">
                <option value="">— select —</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </ModalField>
            <ModalField label="Service category override">
              <input value={form.revenue_calibration.service_category}
                     onChange={(e) => update("revenue_calibration", { ...form.revenue_calibration, service_category: e.target.value })}
                     data-testid="sfa-cal-category" className="modal-input"
                     placeholder="(defaults to industry / category)" />
            </ModalField>
          </div>
        </div>

        {/* Fix 14 — Booking URL injected into the Next Step CTA */}
        <ModalField label="Booking URL (Calendly etc) — overrides default" className="mt-4">
          <input value={form.booking_url} onChange={(e) => update("booking_url", e.target.value)}
                 data-testid="sfa-booking" className="modal-input"
                 placeholder="https://calendly.com/the-entity-agency/30-minute-call" />
        </ModalField>

        {error && (
          <div className="mt-4 text-xs text-[#c46a5a] bg-[#c46a5a]/10 border border-[#c46a5a]/30 px-3 py-2 rounded-sm">
            {error}
          </div>
        )}

        <button onClick={submit} disabled={busy} data-testid="start-full-audit-submit"
                 className="btn-gold text-sm w-full justify-center mt-6">
          <Rocket className="w-4 h-4" />
          {busy ? "Starting…" : "Start Full Audit (admin bypass)"}
        </button>
      </div>

      <style>{`
        .modal-input {
          width: 100%;
          background: #0a0908;
          border: 1px solid #2a2620;
          border-radius: 2px;
          padding: 0.55rem 0.75rem;
          font-size: 0.875rem;
          color: #f4efe6;
        }
        .modal-input::placeholder { color: #5e5448; }
        .modal-input:focus { outline: none; border-color: #b8963e; }
      `}</style>
    </div>
  );
}

function ModalField({ label, children, className }) {
  return (
    <label className={`block ${className || ""}`}>
      <span className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

/**
 * Fix 8 — Visual evidence upload modal. Admin attaches PNG/JPG screenshots
 * to a completed full audit so the PDF's Evidence Appendix renders them.
 */
function EvidenceModal({ open, audit, onClose }) {
  const [items, setItems] = useState([]);
  const [platform, setPlatform] = useState("chatgpt");
  const [prompt, setPrompt] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = React.useCallback(async () => {
    if (!audit?.id) return;
    try {
      const { data } = await api.get(`/admin/audits/${audit.id}/evidence`);
      setItems(data.items || []);
    } catch (e) { /* non-fatal */ }
  }, [audit]);

  React.useEffect(() => {
    if (open) {
      setError(""); setFile(null); setPrompt(""); setCaption(""); setPlatform("chatgpt");
      refresh();
    }
  }, [open, refresh]);

  if (!open || !audit) return null;

  const upload = async () => {
    if (!file) { setError("Pick a screenshot first."); return; }
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("platform", platform);
      fd.append("prompt", prompt);
      fd.append("caption", caption);
      await api.post(`/admin/audits/${audit.id}/evidence`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null); setPrompt(""); setCaption("");
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Upload failed.");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this evidence item? This is permanent.")) return;
    try {
      await api.delete(`/admin/audits/${audit.id}/evidence/${id}`);
      await refresh();
    } catch (e) {
      alert(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div
      data-testid="evidence-modal"
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="eav-card p-7 max-w-3xl w-full max-h-[92vh] overflow-y-auto border border-[#b8963e]/40 bg-[#0c0a08]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="overline text-[#b8963e]">Evidence Appendix</p>
            <h3 className="mt-2 font-serif-display text-2xl text-[#f4efe6]" style={{ fontWeight: 600 }}>
              Upload screenshots — {audit.company_name}
            </h3>
            <p className="mt-1 text-sm text-[#c9bca4]">
              These render on the final PDF's Evidence Appendix page and prove the audit findings.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#1a1815] rounded-sm text-[#8a7e6d]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <ModalField label="Platform">
            <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="modal-input"
                     data-testid="evidence-platform">
              <option value="chatgpt">ChatGPT</option>
              <option value="claude">Claude</option>
              <option value="gemini">Gemini</option>
              <option value="perplexity">Perplexity</option>
            </select>
          </ModalField>
          <ModalField label="Prompt used" className="md:col-span-2">
            <input value={prompt} onChange={(e) => setPrompt(e.target.value)} className="modal-input"
                    data-testid="evidence-prompt"
                    placeholder='What does AI say about {company}?' />
          </ModalField>
        </div>
        <ModalField label="Caption (optional)" className="mt-3">
          <input value={caption} onChange={(e) => setCaption(e.target.value)} className="modal-input"
                  data-testid="evidence-caption"
                  placeholder="Short note explaining what this screenshot proves" />
        </ModalField>
        <ModalField label="Screenshot file (PNG / JPG / WebP, ≤ 8MB)" className="mt-3">
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="evidence-file"
                  className="modal-input file:bg-[#b8963e] file:text-[#0a0908] file:border-0 file:rounded-sm file:px-3 file:py-1 file:mr-3" />
        </ModalField>

        {error && (
          <div className="mt-3 text-xs text-[#c46a5a] bg-[#c46a5a]/10 border border-[#c46a5a]/30 px-3 py-2 rounded-sm">
            {error}
          </div>
        )}
        <button onClick={upload} disabled={busy || !file} data-testid="evidence-upload-submit"
                 className="btn-gold text-sm w-full justify-center mt-4">
          <Upload className="w-4 h-4" />
          {busy ? "Uploading…" : "Upload evidence"}
        </button>

        <div className="mt-7">
          <p className="overline text-[#b8963e]">Uploaded ({items.length})</p>
          {items.length === 0 && (
            <p className="mt-3 text-sm text-[#5e5448] italic">No evidence yet. The Evidence Appendix will display
              "Text evidence captured. Screenshot not available." until you upload at least one.</p>
          )}
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
            {items.map((ev) => (
              <div key={ev.id} className="border border-[#2a2620] rounded-sm overflow-hidden">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${ev.url}`}
                      alt={ev.caption || ev.prompt || ev.platform}
                      className="w-full h-32 object-cover" />
                <div className="p-2">
                  <p className="font-mono-eav text-[9px] tracking-[0.18em] uppercase text-[#b8963e]">{ev.platform}</p>
                  {ev.caption && <p className="text-xs text-[#c9bca4] mt-1 line-clamp-2">{ev.caption}</p>}
                  <button onClick={() => del(ev.id)} data-testid={`evidence-delete-${ev.id}`}
                           className="mt-1 text-[10px] text-[#c46a5a] hover:underline flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Inline PDF preview modal. Hits the admin-only `/admin/audits/{id}/preview`
 * endpoint which streams the same rendered PDF as `/audits/{id}/pdf` but with
 * `Content-Disposition: inline` so the browser embeds it. Bypasses the
 * approval gate so you can sanity-check the deliverable BEFORE flipping
 * `admin_approved_at`.
 */
function PreviewPdfModal({ open, audit, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [qc, setQc]           = useState(null); // Fix 15 quality check result

  React.useEffect(() => {
    let cancelled = false;
    let createdUrl = "";
    const load = async () => {
      if (!open || !audit?.id) return;
      setLoading(true); setError(""); setQc(null);
      try {
        const [pdfRes, qcRes] = await Promise.all([
          api.get(`/admin/audits/${audit.id}/preview`, { responseType: "blob" }),
          api.get(`/admin/audits/${audit.id}/quality-check`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(pdfRes.data);
        setBlobUrl(createdUrl);
        if (qcRes.data) setQc(qcRes.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || "Could not render preview.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, audit]);

  if (!open || !audit) return null;

  const isUnapprovedFull = audit.audit_kind === "full" && !audit.admin_approved_at;

  return (
    <div
      data-testid="preview-pdf-modal"
      className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="eav-card max-w-5xl w-full max-h-[94vh] flex flex-col border border-[#b8963e]/40 bg-[#0c0a08]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 p-5 border-b border-[#2a2620]">
          <div className="min-w-0">
            <p className="overline text-[#b8963e]">
              {isUnapprovedFull ? "Preview · pending approval" : "Preview"}
            </p>
            <h3 className="mt-1 font-serif-display text-xl text-[#f4efe6] truncate" style={{ fontWeight: 600 }}>
              {audit.company_name}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <a href={blobUrl}
                  download={`${audit.company_name.replace(/[^a-z0-9]+/gi, "_")}_audit.pdf`}
                  data-testid="preview-download"
                  className="btn-ghost text-xs">
                <FileDown className="w-4 h-4" /> Download
              </a>
            )}
            <button onClick={onClose} data-testid="preview-close"
                     className="p-1 hover:bg-[#1a1815] rounded-sm text-[#8a7e6d]">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-[#0a0908] relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-mono-eav text-xs tracking-[0.22em] uppercase text-[#8a7e6d] animate-pulse">
                Rendering PDF…
              </span>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
              <p className="text-sm text-[#c46a5a] max-w-md">{error}</p>
            </div>
          )}
          {blobUrl && !loading && !error && (
            <iframe
              data-testid="preview-pdf-iframe"
              src={blobUrl + "#zoom=page-width"}
              title={`${audit.company_name} audit preview`}
              className="w-full h-full"
              style={{ minHeight: "70vh", border: 0 }}
            />
          )}
        </div>

        {isUnapprovedFull && (
          <div className="p-3 px-5 border-t border-[#b8963e]/30 bg-[#b8963e]/10 text-xs text-[#c9bca4]">
            This Full Audit is <strong className="text-[#b8963e]">not yet approved for delivery</strong>. The customer
            cannot see this PDF from their <code>/my-snapshot</code> page until you flip the approval gate or hit
            "Approve & email customer".
          </div>
        )}
        {qc && (
          <div data-testid="preview-quality-check"
                className={`p-3 px-5 border-t text-xs flex items-start gap-3 ${qc.passes ? "border-[#3e8a4d]/40 bg-[#3e8a4d]/10" : "border-[#c46a5a]/40 bg-[#c46a5a]/10"}`}>
            <div className="flex-1 min-w-0">
              <p className={`font-mono-eav text-[10px] tracking-[0.22em] uppercase ${qc.passes ? "text-[#6db66d]" : "text-[#c46a5a]"}`}>
                {qc.passes ? "✓ Quality Check Passed — Ready to Export" : `✗ ${qc.summary}`}
              </p>
              {qc.issues?.length > 0 && (
                <ul className="mt-1 text-[11px] text-[#c9bca4] space-y-0.5">
                  {qc.issues.slice(0, 6).map((iss, idx) => (
                    <li key={idx} className={iss.severity === "block" ? "text-[#c46a5a]" : "text-[#d4a04a]"}>
                      <strong className="font-mono-eav text-[9px] tracking-wider">[{iss.severity.toUpperCase()}]</strong> {iss.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

